import { useCallback, useEffect, useRef, useState } from 'react';
import { logSince } from '../api.js';
import type { LogEntry } from '../api.js';

/**
 * The game's event log: read whole once, then only what is new.
 *
 * Two things need it: the log panel, which shows it, and the stepped
 * playback, which walks the newest entry's events to animate the register.
 * Held here and passed down, so both see exactly the same entries.
 *
 * The first fetch for a game reads every entry, a page at a time, so the
 * panel and its replays reach back to turn 1. Every fetch after that asks
 * only for entries after the newest held, which is also what recovers
 * anything missed while the stream was down. Held NEWEST FIRST, as the
 * readers expect.
 */
export function useGameLog(gameId: number, version: number): {
  entries: LogEntry[];
  /** The register just resolved, which is what playback animates. */
  newest: LogEntry | null;
  loading: boolean;
  /**
   * Whether a fetch for THIS game has come back at least once.
   *
   * The distinction the playback needs, and could not make from `entries`
   * alone: an empty array means either "the log has not arrived yet" or
   * "this game genuinely has no log". Those want opposite behaviour, and
   * treating the first as the second is what made a reload replay the
   * entire game's damage on top of the current total.
   *
   * Set only on a fetch that SUCCEEDED. A log that will not load leaves
   * this false, which parks the playback and lets the board draw straight
   * from server state -- the documented fallback.
   */
  loaded: boolean;
} {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  // Which game the flag above refers to, rather than a bare boolean:
  // switching games must not let the new game inherit the old one's.
  const [loadedFor, setLoadedFor] = useState<number | null>(null);

  // The newest seq held, and the game it belongs to.
  const held = useRef<{ gameId: number; seq: number }>({ gameId, seq: 0 });
  // Fetches run one after another. Two advances close together would
  // otherwise both ask from the same seq and add the same entries twice.
  const queue = useRef<Promise<void>>(Promise.resolve());

  const load = useCallback(() => {
    queue.current = queue.current.then(async () => {
      if (held.current.gameId !== gameId) {
        held.current = { gameId, seq: 0 };
        setEntries([]);
      }
      setLoading(true);
      try {
        const fresh = await logSince(gameId, held.current.seq);
        // Switched games while this was in flight: it answers for the old one.
        if (held.current.gameId !== gameId) return;
        if (fresh.length > 0) {
          held.current = { gameId, seq: fresh[fresh.length - 1].seq };
          setEntries((prev) => {
            const have = new Set(prev.map((e) => e.seq));
            const added = fresh.filter((e) => !have.has(e.seq)).reverse();
            return added.length > 0 ? [...added, ...prev] : prev;
          });
        }
        setLoadedFor(gameId);
      } catch {
        // A log that will not load is not worth interrupting the game for.
        // Playback falls back to the server's positions, and the panel
        // keeps whatever it was already showing.
      } finally {
        setLoading(false);
      }
    });
  }, [gameId]);

  // `version` increments on every advance, so it is exactly the right
  // trigger: one fetch per state change, no more.
  useEffect(() => { load(); }, [load, version]);

  return { entries, newest: entries[0] ?? null, loading, loaded: loadedFor === gameId };
}
